import { useDispatch, useSelector } from "react-redux";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import {
  getTwoFactorAuthSetupData,
  selectLoading,
  selectTwoFactorAuthSetupData,
  selectVerifying,
} from "./TwoFactorAuthenticationSlice";
import OtpInput from "react-otp-input";
import "./TwoFactorAuthentication.scss";
import { toast } from "react-toastify";
import { TwoFactorAuthenticationTypes } from "global/enums";
import {
  getAllValidAuthenticatorTypes,
  getTwoFactorAuthDisplay,
  getTwoFactorAuthText,
  getTwoFactorVerificationFunction,
} from "./TwoFactorAuthHelpers";
import AuthenticatorTypeSelection from "./AuthenticatorTypeSelection";
import { actions } from "../redux/AuthRedux";

const TwoFactorAuthSetupPage = () => {
  const twoFactorAuthSetupData = useSelector(selectTwoFactorAuthSetupData);
  const loading = useSelector(selectLoading);
  const verifying = useSelector(selectVerifying);
  const [oneTimePassword, setOneTimePassword] = useState("");
  const [authenticatorType, setAuthenticatorType] = useState(TwoFactorAuthenticationTypes.Email);

  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();

  useEffect(() => {
    thunkDispatch(getTwoFactorAuthSetupData());
  }, [thunkDispatch]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (verifying) {
      return;
    }

    const verified = (await thunkDispatch(getTwoFactorVerificationFunction(authenticatorType, oneTimePassword)))
      .payload;
    if (verified) {
      window.location.href = "/dashboard";
    } else {
      toast.error("Invalid code");
    }
  };

  const logout: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void = () => {
    event?.preventDefault();
    dispatch(actions.logout());
  };

  const onAuthenticationTypeChanged = (e: ChangeEvent<HTMLSelectElement>) => {
    const index = parseInt(e.currentTarget.value);
    setAuthenticatorType(index);
  };

  return (
    <div className="d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed">
      {/* begin::Content */}
      <div className="d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20">
        {/* begin::Wrapper */}
        <div className="w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto">
          <ActivityIndicator isBusy={loading}>
            <div className="card-header">
              <div className="card-title m-0">
                <h3 className="fw-bolder m-0">Two Factor Authentication Setup</h3>
              </div>
            </div>
            <div className="card-body border-top p-6 d-flex flex-column gap-4 justify-content-center">
              <AuthenticatorTypeSelection
                onTypeChanged={onAuthenticationTypeChanged}
                availableAuthenticatorTypes={getAllValidAuthenticatorTypes()}
                data-test-id="two-factor-setup-form__authenticator-type-select"
              />
              {twoFactorAuthSetupData && (
                <Form className="d-flex flex-column" style={{ height: 500 }} onSubmit={onSubmit}>
                  {getTwoFactorAuthDisplay(twoFactorAuthSetupData, authenticatorType)}
                  <div className="separator mb-2 mt-auto" />
                  <div className="verification-code-section">
                    <div className="verification-code-label">
                      Enter verification code from {getTwoFactorAuthText(authenticatorType)}:{" "}
                    </div>
                    <OtpInput
                      inputStyle="inputStyle"
                      value={oneTimePassword}
                      onChange={setOneTimePassword}
                      numInputs={6}
                      inputType="number"
                      renderSeparator={(index) => (index === 2 ? <span>-</span> : "")}
                      renderInput={(props, index) => (
                        <input {...props} data-test-id={`two-factor-setup-form__code-input-${index}`} />
                      )}
                    />
                  </div>
                  <div className="d-flex justify-content-between">
                    <button
                      onClick={logout}
                      className="cpl-btn cpl-btn-danger mt-2 text-white"
                      data-test-id="two-factor-setup-form__logout-button"
                    >
                      Logout
                    </button>
                    <button
                      type="submit"
                      className="cpl-btn cpl-btn-primary mt-2"
                      data-test-id="two-factor-setup-form__submit-button"
                    >
                      Continue
                    </button>
                  </div>
                </Form>
              )}
            </div>
          </ActivityIndicator>
        </div>
        {/* end::Wrapper */}
      </div>
      {/* end::Content */}
    </div>
  );
};

export default TwoFactorAuthSetupPage;
